import { useAtom, useAtomValue } from 'jotai'
import { useNavigate } from 'react-router'
import { authenticationState } from '@repo/unicity'
import { cartAtom, cartFirstItemAtom, cartTotalItemsAtom, type CartItems } from './cart.store'
import { fgProductsAtom } from '@/core/feelgreat/feelgreat.store'
import { selectedFlavorAtom, selectedPurchaseOptionAtom } from '@/core/products/products.store'
import { checkoutReferralInfoAtom } from '@/core/checkout/checkout.store'
import { countryFeaturesAtom } from '@/core/store/country-features.store'
import { analytics } from '@/core/analytics/analytics'
import type { FGProducts } from '@/core/feelgreat/feelgreat.types'

export type PriceMode = 'retail' | 'member'

export const usePriceMode = (): PriceMode => {
    const authentication = useAtomValue(authenticationState)
    const referralInfo = useAtomValue(checkoutReferralInfoAtom)
    const isAuthenticated = Boolean(authentication.token && authentication.baId)
    const hasReferral = Boolean(referralInfo)
    return isAuthenticated || hasReferral ? 'member' : 'retail'
}

export const getProductPrice = (fgProducts: FGProducts, flavor: string, purchaseType: string, mode: PriceMode): number => {
    const product = fgProducts[flavor]?.[purchaseType]
    if (!product) return 0
    return mode === 'member' ? (product.price.member ?? product.price.retail) : product.price.retail
}

export const getItemCode = (fgProducts: FGProducts, flavor: string, purchaseType: string): string => fgProducts[flavor]?.[purchaseType]?.item_code ?? ''

export const formatPrice = (amount: number, currencyCode: string): string => {
    if (!amount) return '-'
    return `${amount.toLocaleString('en-US')} ${currencyCode}`
}

export const useCart = () => {
    const [cart, setCart] = useAtom(cartAtom)
    const cartTotal = useAtomValue(cartTotalItemsAtom)
    const cartFirstItem = useAtomValue(cartFirstItemAtom)
    const fgProducts = useAtomValue(fgProductsAtom)
    const navigate = useNavigate()
    const authentication = useAtomValue(authenticationState)
    const referralInfo = useAtomValue(checkoutReferralInfoAtom)
    const { guestLogicA } = useAtomValue(countryFeaturesAtom)

    const isAuthenticated = Boolean(authentication.token && authentication.baId)
    const priceMode = usePriceMode()

    const addToCart = (flavor: string, purchaseType: string, qty: number = 1) => {
        analytics.track('cartItemAdded', {
            item_code: getItemCode(fgProducts, flavor, purchaseType),
            flavor,
            purchase_type: purchaseType,
            qty,
            price_mode: priceMode
        })
        setCart((prev: CartItems) => {
            const existing = prev[purchaseType]?.[flavor] ?? 0
            return {
                ...prev,
                [purchaseType]: {
                    ...prev[purchaseType],
                    [flavor]: existing + qty
                }
            }
        })
    }

    const removeFromCart = (purchaseType: string, flavor: string) => {
        analytics.track('cartItemRemoved', {
            item_code: getItemCode(fgProducts, flavor, purchaseType),
            flavor,
            purchase_type: purchaseType
        })
        setCart((prev: CartItems) => {
            const next = { ...prev }
            if (next[purchaseType]) {
                const flavors = { ...next[purchaseType] }
                delete flavors[flavor]
                if (Object.keys(flavors).length === 0) delete next[purchaseType]
                else next[purchaseType] = flavors
            }
            return next
        })
    }

    const clearCart = () => setCart({})

    const cartPrice = cartFirstItem ? getProductPrice(fgProducts, cartFirstItem.flavor, cartFirstItem.purchaseType, priceMode) : 0

    const goToCheckout = () => {
        // Top of the checkout funnel — every abandonment further down is measured against it.
        const destination = guestLogicA && !isAuthenticated && !referralInfo ? '/login' : isAuthenticated ? '/checkout/shipping' : '/checkout/create-account'
        analytics.track('cartCheckoutClicked', {
            destination,
            cart_item_count: cartTotal,
            has_referral: Boolean(referralInfo)
        })
        navigate(destination)
    }

    return {
        cart,
        cartTotal,
        cartFirstItem,
        cartPrice,
        priceMode,
        addToCart,
        removeFromCart,
        clearCart,
        goToCheckout
    }
}

/** Selector hook for product page */
export const useProductSelection = () => {
    const [selectedFlavor, setSelectedFlavor] = useAtom(selectedFlavorAtom)
    const [selectedPurchaseOption, setSelectedPurchaseOption] = useAtom(selectedPurchaseOptionAtom)
    const fgProducts = useAtomValue(fgProductsAtom)

    const priceMode = usePriceMode()

    const selectedPrice = getProductPrice(fgProducts, selectedFlavor, selectedPurchaseOption, priceMode)
    const retailPrice = getProductPrice(fgProducts, selectedFlavor, selectedPurchaseOption, 'retail')

    return {
        selectedFlavor,
        setSelectedFlavor,
        selectedPurchaseOption,
        setSelectedPurchaseOption,
        selectedPrice,
        retailPrice,
        priceMode
    }
}
